import { z } from 'zod';
import { AiModelTier, UsdAmount } from './ai.js';
import { CHILLDOC_IMAGE_ID } from './chilldoc-images.js';
import { ImageDescription, ImageFocal as ImageFocalSchema, } from './image-description.js';
import { ChartSnapshot, ChartSource, ChartSpec } from './chilldoc-chart.js';
/**
 * Trình chiếu trong `.chilldoc` — APM-SLD-a, docs/plan-slides.md §3, §5.2, §7.2.
 *
 * Slide nằm trong thuộc tính `slides` của node `slideBreak` (thanh ngăn): mỗi
 * thanh ngăn mở một **vùng**, vùng chạy tới thanh ngăn kế tiếp hoặc hết tài
 * liệu. Cài đặt của cả bộ (tỉ lệ, chủ đề) nằm ở vỏ tệp (`ChilldocPresentation`).
 *
 * Hai thứ tách bạch, như khu vực vẽ:
 *
 * - `Slide` / `SlideBox` — cái lưu trong tệp: ô có toạ độ trong **khung logic**
 *   (16:9 = 1280×720, 4:3 = 960×720), nội dung ô chữ là JSON TipTap. Đọc
 *   **lỏng** (`readSlides`): ô/slide hỏng bị bỏ, toạ độ lệch bị kẹp — một slide
 *   hỏng không được làm cả tệp không mở được.
 * - `SlideDeckSpec` — cái AI (và "Tạo slide từ vùng") sinh ra: layout + chữ,
 *   **không toạ độ**. Dựng thành `Slide` là việc của hàm tất định ở web.
 */
// ---------------------------------------------------------------------------
// Tỉ lệ và khung logic
// ---------------------------------------------------------------------------
export const SLIDE_ASPECTS = ['16:9', '4:3'];
export const SlideAspect = z.enum(SLIDE_ASPECTS);
/** Khung logic theo tỉ lệ — mọi toạ độ của ô tính trong khung này; vẽ ở cỡ nào cũng `scale()`. */
export const SLIDE_FRAME = {
    '16:9': { width: 1280, height: 720 },
    '4:3': { width: 960, height: 720 },
};
/** Cạnh tối thiểu của một ô (px logic). */
export const SLIDE_BOX_MIN = 24;
export const MAX_SLIDES_PER_REGION = 30;
export const MAX_SLIDES = 200;
/**
 * Trần ô của một slide. 16 → **32** ở APM-CD2-m: một sơ đồ nhanh 6 bước là 11 ô (6 hình + 5
 * mũi tên), cộng tiêu đề và một ô chữ là 13 — 16 không còn chỗ cho sơ đồ thứ hai hay vài
 * mũi tên chú thích. Ô là JSON nhỏ, vẽ bằng DOM/SVG; 32 vẫn rẻ ở cả thumbnail.
 */
export const MAX_BOXES_PER_SLIDE = 32;
/** Tổng chữ của một slide (ký tự) — slide không phải chỗ chép nguyên một trang. */
export const MAX_SLIDE_TEXT = 4000;
// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export const SLIDE_LAYOUTS = [
    'title',
    'titleContent',
    'sectionHeader',
    'twoContent',
    'comparison',
    'titleOnly',
    'blank',
    'contentCaption',
    'pictureCaption',
    'contentPicture',
    'titlePicture',
];
export const SlideLayout = z.enum(SLIDE_LAYOUTS);
/** Tên tiếng Việt — cho markdown đọc được và prompt. Giao diện dùng i18n. */
export const SLIDE_LAYOUT_LABELS = {
    title: 'Trang tiêu đề',
    titleContent: 'Tiêu đề và nội dung',
    sectionHeader: 'Đầu phần',
    twoContent: 'Hai nội dung',
    comparison: 'So sánh',
    titleOnly: 'Chỉ tiêu đề',
    blank: 'Trống',
    contentCaption: 'Nội dung có chú thích',
    pictureCaption: 'Ảnh có chú thích',
    contentPicture: 'Nội dung và ảnh',
    titlePicture: 'Trang tiêu đề có ảnh',
};
/**
 * Vai của ô. `heading` = đầu cột của layout So sánh; `text` = ô chữ tự do
 * (người dùng thêm, hoặc ô cũ không còn chỗ khi đổi layout); `shape` = hình khối /
 * đường nối (APM-CD2-m) — hình ở `SlideBox.shape`, chữ trong hình ở `content`;
 * `table` = bảng (APM-CD2-n) — `content` là doc chứa **một** node `table` của schema
 * chilldoc; `chart` = biểu đồ — `SlideBox.chart`, bảng số liệu (bản sao) ở `content`.
 */
export const SLIDE_BOX_ROLES = [
    'title',
    'subtitle',
    'heading',
    'body',
    'caption',
    'text',
    'image',
    'shape',
    'table',
    'chart',
];
export const SlideBoxRole = z.enum(SLIDE_BOX_ROLES);
// ---------------------------------------------------------------------------
// Slide trong tệp
// ---------------------------------------------------------------------------
const SlideId = z.string().trim().min(1).max(40);
/** JSON TipTap của ô chữ — như `doc` của vỏ: chỉ kiểm vỏ ngoài, phần trong để schema của editor kiểm. */
export const SlideBoxContent = z.object({ type: z.literal('doc') }).loose();
const coord = z.number().refine(Number.isFinite).transform(Math.round);
/**
 * "Nguồn" của một ô khi tài liệu mở từ `.pptx` — APM-PPE-c, plan-office-editors §8.2.
 * Lúc Lưu, bộ vá tìm lại đúng chỗ trong tệp gốc để chỉ ghi phần đã đổi:
 * - `shape`: `p:cNvPr id` của `p:sp` / `p:pic`;
 * - `diagram`: part dữ liệu SmartArt (`ppt/diagrams/data1.xml`) + `modelId` các nút theo
 *   đúng thứ tự mục đã xuất ra danh sách;
 * - `table`: `p:cNvPr id` của `p:graphicFrame` chứa `a:tbl`.
 * Ô không có `source` là ô Chill tạo mới. Tệp `.chilldoc` thường không có trường này.
 *
 * @deprecated APM-PPX-i: `.pptx` không còn mở thành slide của Chill (plan-pptx-editor §8) nên
 * không ai ghi trường này nữa. Giữ để tệp/nháp cũ còn đọc được (`catch(undefined)`).
 */
export const SlideBoxSource = z.union([
    z.object({ shape: z.number().int().nonnegative() }),
    z.object({ diagram: z.string().min(1).max(200), points: z.array(z.string().min(1)).max(200) }),
    z.object({ table: z.number().int().nonnegative() }),
]);
const hash8 = z.string().regex(/^[0-9a-f]{8}$/);
/**
 * Băm nội dung ô để biết "đã đổi hay chưa" (`srcHash`): JSON **chuẩn hoá** (khoá sắp xếp,
 * không khoảng trắng) rồi FNV-1a như `regionHash`. Cùng một hàm ở lúc mở và lúc Lưu —
 * editor có thể xếp lại thứ tự khoá/mark, nên không được băm chuỗi JSON thô.
 */
export function srcHashOf(content) {
    return regionHash(canonicalJson(content));
}
function canonicalJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    if (value && typeof value === 'object') {
        const entries = Object.entries(value)
            .filter(([, v]) => v !== undefined)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`);
        return `{${entries.join(',')}}`;
    }
    return JSON.stringify(value) ?? 'null';
}
// ---------------------------------------------------------------------------
// Hình khối — APM-CD2-m, plan-chilldoc-v2 §10
// ---------------------------------------------------------------------------
/** Màu theo chủ đề — đổi chủ đề thì hình đổi màu theo, như "Theme colors" của PowerPoint. */
export const SLIDE_SHAPE_THEME_COLORS = [
    'accent',
    'surface',
    'title',
    'text',
    'muted',
    'background',
];
/** Màu của hình: tên màu chủ đề, hoặc `#rrggbb` cố định. */
export const SlideShapeColor = z.union([
    z.enum(SLIDE_SHAPE_THEME_COLORS),
    z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .transform((hex) => hex.toLowerCase()),
]);
/** Đầu mũi tên của đường nối: ở điểm cuối, điểm đầu, cả hai, hay không. */
export const SLIDE_SHAPE_ENDS = ['none', 'end', 'start', 'both'];
/** Preset là **đường** (vẽ từ góc trên-trái tới góc dưới-phải của ô, lật để đổi hướng). */
export const SLIDE_LINE_PRESETS = new Set(['line', 'straightConnector1']);
export const SLIDE_SHAPE_LINE_MAX = 48;
/** Góc bất kỳ ⇒ `[0, 360)`, làm tròn 0,1°. */
export function normalizeRotation(degrees) {
    const turned = ((degrees % 360) + 360) % 360;
    const rounded = Math.round(turned * 10) / 10;
    return rounded >= 360 ? 0 : rounded;
}
/**
 * Hình của một ô `shape`. Mọi trường đọc **lỏng** (`catch`): trường hỏng về mặc định, không
 * kéo cả ô (và slide) đi theo — như mọi thứ khác trong `SlideBox`.
 *
 * - `preset`: tên ECMA-376 `ST_ShapeType` (`rect`, `rightArrow`, `wedgeRectCallout`…); tên
 *   bộ vẽ không biết ⇒ vẽ hình chữ nhật, **giữ** tên (không ghi đè lựa chọn của người dùng).
 * - `adj`: giá trị chỉnh (`avLst`) theo đơn vị ECMA (`{ adj: 25000 }`); thiếu ⇒ mặc định.
 * - `fill` / `line`: `null` = không nền / không viền.
 * - `rotation`: độ, theo chiều kim đồng hồ quanh tâm ô; `flipH` / `flipV` lật hình (chữ không lật).
 */
export const SlideShape = z.object({
    preset: z
        .string()
        .regex(/^[A-Za-z][A-Za-z0-9]{0,39}$/)
        .catch('rect')
        .default('rect'),
    adj: z
        .record(z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,15}$/), z.number().refine(Number.isFinite))
        .refine((record) => Object.keys(record).length <= 8)
        .optional()
        .catch(undefined),
    fill: SlideShapeColor.nullable().catch('accent').default('accent'),
    line: SlideShapeColor.nullable().catch(null).default(null),
    lineWidth: z.number().min(0).max(SLIDE_SHAPE_LINE_MAX).catch(2).default(2),
    rotation: z.number().refine(Number.isFinite).transform(normalizeRotation).catch(0).default(0),
    flipH: z.boolean().catch(false).default(false),
    flipV: z.boolean().catch(false).default(false),
    ends: z.enum(SLIDE_SHAPE_ENDS).catch('none').default('none'),
});
export const DEFAULT_SLIDE_SHAPE = SlideShape.parse({});
/** Hình của ô — ô `shape` thiếu / hỏng trường `shape` vẽ hình chữ nhật mặc định. */
export function shapeOf(box) {
    return box.shape ?? DEFAULT_SLIDE_SHAPE;
}
/** Ô là đường nối (không chữ, kéo hai đầu thay cho tám tay nắm). */
export function isLineBox(box) {
    return box.role === 'shape' && SLIDE_LINE_PRESETS.has(shapeOf(box).preset);
}
// ---------------------------------------------------------------------------
// Bảng và biểu đồ — APM-CD2-n, plan-chilldoc-v2 §11
// ---------------------------------------------------------------------------
/**
 * Cách đọc bảng số liệu của ô biểu đồ — `ChartSource` bỏ `tableId`: bảng nằm ngay trong
 * `content` của ô (bản sao lúc chèn), không trỏ vào bảng của tài liệu.
 */
export const SlideChartRead = ChartSource.omit({ tableId: true });
const ChartHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/**
 * Biểu đồ trong slide. **Bản sao, không liên kết sống** (plan §11): sửa bảng trong văn bản
 * không đổi biểu đồ trên slide. Số vẽ tính từ bảng trong `content` theo `read`; không có
 * bảng / `read` ⇒ vẽ từ `snapshot` (biểu đồ tĩnh chép từ văn bản mà mất bảng nguồn).
 * Đọc lỏng như `ChartAttrs`: trường hỏng về `null`, không kéo cả ô đi theo.
 */
export const SlideChart = z.object({
    spec: ChartSpec.nullable().catch(null),
    read: SlideChartRead.nullable().catch(null).default(null),
    snapshot: ChartSnapshot.nullable().catch(null).default(null),
    /** `null` ⇒ màu theo chủ đề slide (màu nhấn trước, rồi bảng màu biểu đồ mặc định). */
    palette: z.array(ChartHex).min(1).max(12).nullable().catch(null).default(null),
});
/** Node `table` đầu tiên ở cấp cao nhất của `content` (ô bảng / số liệu của ô biểu đồ). */
export function slideTableOf(content) {
    const blocks = content?.content;
    if (!Array.isArray(blocks))
        return null;
    return blocks.find((node) => node && node.type === 'table') ?? null;
}
export const SlideBox = z.object({
    id: SlideId,
    role: SlideBoxRole,
    x: coord,
    y: coord,
    w: coord,
    h: coord,
    content: SlideBoxContent.nullable().catch(null).default(null),
    fileId: z.string().trim().min(1).max(64).nullable().catch(null).default(null),
    /**
     * Ảnh **nhúng** (APM-SLD-l / CDIMG-d): trỏ vào khối ảnh cuối tệp, như node
     * `image` của tài liệu — ảnh lấy từ vùng ngăn dùng **chung** khối với tài liệu,
     * không chép. Có `imageId` thì không có `fileId` và ngược lại.
     */
    imageId: z.string().regex(CHILLDOC_IMAGE_ID).nullable().catch(null).default(null),
    /** Rộng / cao của ảnh gốc — để đặt lại ô theo ảnh khi đổi layout mà không phải tải ảnh. */
    ratio: z.number().min(0.05).max(20).nullable().catch(null).default(null),
    fit: z.enum(['contain', 'cover']).catch('contain').default('contain'),
    /**
     * Điểm neo khi `fit: 'cover'` (APM-SLD-o): chủ thể chính của ảnh nằm đâu, lấy
     * từ mô tả vision (`ImageDescription.focal`) lúc đặt ảnh. `null` = giữa.
     */
    focal: ImageFocalSchema.nullable().catch(null).default(null),
    /**
     * Nguồn trong tệp Office gốc (APM-PPE-c). Rác ⇒ như không có.
     * @deprecated APM-PPX-i — chỉ còn để đọc tệp cũ; `.pptx` sửa bằng hộp soạn PowerPoint.
     */
    source: SlideBoxSource.optional().catch(undefined),
    /**
     * `srcHashOf(content)` lúc mở từ tệp gốc — khớp lúc Lưu ⇒ không chạm shape ấy.
     * @deprecated APM-PPX-i — cùng lý do với `source`.
     */
    srcHash: hash8.optional().catch(undefined),
    /** Hình của ô `shape` (APM-CD2-m). Ô vai khác bỏ qua trường này; rác ⇒ như không có. */
    shape: SlideShape.optional().catch(undefined),
    /** Biểu đồ của ô `chart` (APM-CD2-n). Ô vai khác bỏ qua trường này; rác ⇒ như không có. */
    chart: SlideChart.optional().catch(undefined),
});
/**
 * Cách đặt ảnh vào khung theo mô tả — APM-SLD-o, plan-slides §3.11. Tất định, không
 * để model quyết pixel: **ảnh chụp** không nhiều chữ mới được `cover` (crop theo
 * `focal`); sơ đồ, biểu đồ, ảnh màn hình, ảnh chữ, logo, và ảnh **chưa có mô tả** luôn
 * `contain` — không đoán. Không bao giờ kéo giãn: chỉ có crop hoặc viền.
 */
export function cropOf(description) {
    if (description && description.kind === 'photo' && !description.textHeavy)
        return { fit: 'cover', focal: description.focal };
    return { fit: 'contain', focal: null };
}
export const SLIDE_ORIGINS = ['manual', 'outline', 'ai'];
export const Slide = z.object({
    id: SlideId,
    layout: SlideLayout.catch('blank'),
    boxes: z.array(SlideBox).max(MAX_BOXES_PER_SLIDE),
    origin: z.enum(SLIDE_ORIGINS).catch('manual').default('manual'),
    /** Băm markdown của vùng lúc tạo (`regionHash`) — lệch ⇒ "Nội dung vùng đã đổi". `manual` ⇒ null. */
    sourceHash: z
        .string()
        .regex(/^[0-9a-f]{8}$/)
        .nullable()
        .catch(null)
        .default(null),
    /**
     * Part của slide trong `.pptx` gốc (`ppt/slides/slide3.xml`) — APM-PPE-c. Không phải
     * `sourceHash` ở trên (băm vùng markdown của APM-SLD): tên gần nhau, nghĩa khác nhau.
     * @deprecated APM-PPX-i — chỉ còn để đọc tệp cũ; `.pptx` sửa bằng hộp soạn PowerPoint.
     */
    source: z
        .object({ part: z.string().min(1).max(200) })
        .optional()
        .catch(undefined),
});
/**
 * Kẹp một ô vào trong slide, cạnh ≥ `SLIDE_BOX_MIN`. Đường nối (`isLineBox`) được phép
 * mỏng tới 0 — một mũi tên ngang có ô cao 0; ép 24 là bẻ nó thành đường chéo.
 */
export function clampBox(box, aspect) {
    const frame = SLIDE_FRAME[aspect];
    const record = box;
    const min = record.role === 'shape' && isLineBox({ role: 'shape', shape: record.shape })
        ? 0
        : SLIDE_BOX_MIN;
    const w = Math.min(frame.width, Math.max(min, Math.round(box.w)));
    const h = Math.min(frame.height, Math.max(min, Math.round(box.h)));
    const x = Math.min(frame.width - w, Math.max(0, Math.round(box.x)));
    const y = Math.min(frame.height - h, Math.max(0, Math.round(box.y)));
    return { ...box, x, y, w, h };
}
/**
 * Đọc `slides` của một thanh ngăn — **không bao giờ ném**. Ô hỏng hình bị bỏ
 * (không kéo cả slide theo), slide hỏng bị bỏ, toạ độ bị kẹp, id trùng trong
 * vùng bị bỏ bản sau, quá trần bị cắt.
 */
export function readSlides(raw, aspect) {
    if (!Array.isArray(raw))
        return [];
    const seen = new Set();
    const slides = [];
    for (const item of raw) {
        if (slides.length >= MAX_SLIDES_PER_REGION)
            break;
        if (!item || typeof item !== 'object')
            continue;
        const rawBoxes = item.boxes;
        const boxes = [];
        const boxIds = new Set();
        for (const rawBox of Array.isArray(rawBoxes) ? rawBoxes : []) {
            if (boxes.length >= MAX_BOXES_PER_SLIDE)
                break;
            const box = SlideBox.safeParse(rawBox);
            if (!box.success || boxIds.has(box.data.id))
                continue;
            boxIds.add(box.data.id);
            boxes.push(clampBox(box.data, aspect));
        }
        const slide = Slide.safeParse({ ...item, boxes });
        if (!slide.success || seen.has(slide.data.id))
            continue;
        seen.add(slide.data.id);
        slides.push(slide.data);
    }
    return slides;
}
// ---------------------------------------------------------------------------
// Cài đặt cả bộ — trong vỏ tệp
// ---------------------------------------------------------------------------
export const SLIDE_THEME_IDS = [
    'light',
    'dark',
    'office',
    'warm',
    'minimal',
    'contrast',
    'glass',
    'glassDark',
];
export const SlideThemeId = z.enum(SLIDE_THEME_IDS);
/**
 * Tỉ lệ và chủ đề của cả bộ. Tuỳ chọn trong vỏ: tệp cũ không có ⇒ 16:9, Sáng.
 * Tệp có slide mở ở bản app **cũ** hơn thì mất slide (node `slideBreak` lạ bị
 * schema của editor loại) — vì thế không nâng `CHILLDOC_VERSION`: bản cũ vẫn mở
 * được chữ, đổi lại là không giữ slide nếu lưu đè từ bản cũ.
 */
export const ChilldocPresentation = z.object({
    aspect: SlideAspect.catch('16:9').default('16:9'),
    theme: SlideThemeId.catch('light').default('light'),
    /**
     * Slide của hai vùng **ẩn** đầu và cuối tài liệu (APM-SLD-k): trang tiêu đề,
     * trang kết. Không có thanh ngăn nào trong chữ — nằm ở vỏ như cài đặt, đọc
     * **thô** như `slides` của thanh ngăn (`readSlides` lúc vẽ). Rác ⇒ như không có,
     * không kéo tỉ lệ / chủ đề đi theo.
     */
    opening: z.array(z.unknown()).max(MAX_SLIDES_PER_REGION).optional().catch(undefined),
    closing: z.array(z.unknown()).max(MAX_SLIDES_PER_REGION).optional().catch(undefined),
});
export const DEFAULT_PRESENTATION = { aspect: '16:9', theme: 'light' };
export const SLIDE_THEMES = {
    light: {
        label: 'Sáng',
        background: '#ffffff',
        surface: '#eef4ff',
        title: '#1f2937',
        text: '#374151',
        muted: '#6b7280',
        accent: '#2563eb',
        titleBand: null,
        titleOnBand: '#1f2937',
        titleFont: 'Be Vietnam Pro',
        bodyFont: 'Be Vietnam Pro',
        titleWeight: 700,
    },
    dark: {
        label: 'Tối',
        background: '#111827',
        surface: '#1f2937',
        title: '#f9fafb',
        text: '#e5e7eb',
        muted: '#9ca3af',
        accent: '#2dd4bf',
        titleBand: null,
        titleOnBand: '#f9fafb',
        titleFont: 'Inter',
        bodyFont: 'Inter',
        titleWeight: 600,
    },
    office: {
        label: 'Công sở',
        background: '#ffffff',
        surface: '#e8eef7',
        title: '#1e3a5f',
        text: '#243b53',
        muted: '#627d98',
        accent: '#1e3a5f',
        titleBand: '#1e3a5f',
        titleOnBand: '#ffffff',
        titleFont: 'Montserrat',
        bodyFont: 'Inter',
        titleWeight: 600,
    },
    warm: {
        label: 'Ấm',
        background: '#fdf8f0',
        surface: '#f5e6d3',
        title: '#5c2e0e',
        text: '#3f2a1d',
        muted: '#7c5e48',
        accent: '#b45309',
        titleBand: null,
        titleOnBand: '#5c2e0e',
        titleFont: 'Lora',
        bodyFont: 'Be Vietnam Pro',
        titleWeight: 700,
    },
    minimal: {
        label: 'Tối giản',
        background: '#ffffff',
        surface: '#f4f4f5',
        title: '#09090b',
        text: '#27272a',
        muted: '#52525b',
        accent: '#09090b',
        titleBand: null,
        titleOnBand: '#09090b',
        titleFont: 'Inter',
        bodyFont: 'Inter',
        titleWeight: 300,
    },
    contrast: {
        label: 'Tương phản cao',
        background: '#000000',
        surface: '#1a1a1a',
        title: '#ffd60a',
        text: '#ffffff',
        muted: '#d4d4d4',
        accent: '#ffd60a',
        titleBand: null,
        titleOnBand: '#ffd60a',
        titleFont: 'Be Vietnam Pro',
        bodyFont: 'Be Vietnam Pro',
        titleWeight: 700,
    },
    /*
      Liquid Glass — cùng chất liệu với landing và sổ chốt phương án (`public-shell.css`):
      ba đốm xanh / tím / ngọc nhoè trên nền xám xanh nhạt, nội dung trên một tấm kính bo
      28px, font Saira. Hai bản như hai chế độ sáng / tối của landing — slide không đổi
      theo giao diện app, nên người trình bày tự chọn (người dùng yêu cầu 2026-10-16).
    */
    glass: {
        label: 'Liquid Glass',
        background: '#f3f5fa',
        surface: 'rgb(255 255 255 / 0.6)',
        title: '#0b1020',
        text: '#1f2638',
        muted: '#5a6275',
        accent: '#2460d8',
        titleBand: null,
        titleOnBand: '#0b1020',
        titleFont: 'Saira Semi Condensed',
        bodyFont: 'Saira',
        titleWeight: 600,
        backdrop: [
            'radial-gradient(circle at 6% 4%, rgb(155 192 255 / 0.9), transparent 42%)',
            'radial-gradient(circle at 98% 52%, rgb(210 180 255 / 0.85), transparent 42%)',
            'radial-gradient(circle at 34% 108%, rgb(154 230 236 / 0.85), transparent 46%)',
            'radial-gradient(120% 100% at 50% -10%, #e9edf6, #f3f5fa 60%)',
        ].join(', '),
        panel: {
            inset: 32,
            radius: 28,
            fill: 'linear-gradient(135deg, rgb(255 255 255 / 0.55), rgb(255 255 255 / 0.28))',
            border: '1px solid rgb(255 255 255 / 0.9)',
            shadow: 'inset 0 1px 0 rgb(255 255 255), inset 0 -1px 0 rgb(0 0 0 / 0.12), 0 30px 80px rgb(30 45 90 / 0.18)',
        },
    },
    glassDark: {
        label: 'Liquid Glass tối',
        background: '#06080f',
        surface: 'rgb(255 255 255 / 0.1)',
        title: '#f3f5fb',
        text: '#dfe3ee',
        muted: '#a3abc0',
        accent: '#7aa7ff',
        titleBand: null,
        titleOnBand: '#f3f5fb',
        titleFont: 'Saira Semi Condensed',
        bodyFont: 'Saira',
        titleWeight: 600,
        backdrop: [
            'radial-gradient(circle at 6% 4%, rgb(45 108 255 / 0.7), transparent 42%)',
            'radial-gradient(circle at 98% 52%, rgb(138 60 255 / 0.62), transparent 42%)',
            'radial-gradient(circle at 34% 108%, rgb(19 164 180 / 0.62), transparent 46%)',
            'radial-gradient(120% 100% at 50% -10%, #0c1020, #06080f 60%)',
        ].join(', '),
        panel: {
            inset: 32,
            radius: 28,
            fill: 'linear-gradient(135deg, rgb(255 255 255 / 0.12), rgb(255 255 255 / 0.06))',
            border: '1px solid rgb(255 255 255 / 0.16)',
            shadow: 'inset 0 1px 0 rgb(255 255 255 / 0.5), inset 0 -1px 0 rgb(0 0 0 / 0.18), 0 30px 80px rgb(0 0 0 / 0.45)',
        },
    },
};
/** Giá trị CSS `background` của slide: ảnh nền (nếu chủ đề có) phủ trên màu nền đặc. */
export function slideThemeBackground(theme) {
    return theme.backdrop ? `${theme.backdrop}, ${theme.background}` : theme.background;
}
/** Cỡ chữ mặc định theo vai (px logic) — web đặt bằng CSS, không ghi vào `content`. */
export function slideFontSize(role, layout) {
    // Ô tiêu đề của Nội dung có chú thích hẹp (420px) — 44px là ba dòng cho một tiêu đề bình thường.
    if (role === 'title')
        return layout === 'title' ? 56 : layout === 'contentCaption' ? 32 : 44;
    if (role === 'subtitle')
        return 28;
    if (role === 'heading')
        return 28;
    return 24;
}
/**
 * Cỡ chữ của **ý sinh tự động** theo cấp (px logic) — người dùng yêu cầu 2026-10-02:
 * ý chính 32, ý con 28, sâu hơn nữa 24, và không bao giờ dưới 24. Khác `slideFontSize`
 * (cỡ mặc định của ô, đặt bằng CSS), cỡ này **ghi vào `content`** dạng mark
 * `textStyle.fontSize` ở `bulletsDoc` — chỉ bộ slide sinh ra sau mới nhận, bộ đã có
 * không đổi cỡ sau lưng người dùng. Sức chứa (`slideCapacity`) đo ở cỡ của ý chính.
 */
export const SLIDE_BULLET_FONT_SIZES = [32, 28, 24];
export const SLIDE_BULLET_FONT_MIN = 24;
export function slideBulletFontSize(level) {
    const index = Math.max(0, Math.min(Math.trunc(level), SLIDE_BULLET_FONT_SIZES.length - 1));
    return Math.max(SLIDE_BULLET_FONT_MIN, SLIDE_BULLET_FONT_SIZES[index] ?? SLIDE_BULLET_FONT_MIN);
}
/** Bảng toạ độ ở 16:9 (plan §5.2), lề 64px. 4:3 dựng bằng `reflowAspect` từ đây — một nguồn toạ độ. */
const LAYOUT_CELLS = {
    title: [
        ['title', 64, 220, 1152, 160],
        ['subtitle', 64, 400, 1152, 90],
    ],
    titleContent: [
        ['title', 64, 40, 1152, 110],
        ['body', 64, 170, 1152, 500],
    ],
    sectionHeader: [
        ['title', 64, 300, 1152, 140],
        ['subtitle', 64, 450, 1152, 80],
    ],
    twoContent: [
        ['title', 64, 40, 1152, 110],
        ['body', 64, 170, 560, 500],
        ['body', 656, 170, 560, 500],
    ],
    comparison: [
        ['title', 64, 40, 1152, 110],
        ['heading', 64, 170, 560, 60],
        ['heading', 656, 170, 560, 60],
        ['body', 64, 240, 560, 430],
        ['body', 656, 240, 560, 430],
    ],
    titleOnly: [['title', 64, 40, 1152, 110]],
    blank: [],
    contentCaption: [
        ['title', 64, 40, 420, 120],
        ['caption', 64, 170, 420, 500],
        ['body', 520, 40, 696, 630],
    ],
    // Ảnh có chú thích: tiêu đề + ảnh + chú thích. Ô ảnh dưới đây là chỗ **tối đa**; có
    // tỉ lệ ảnh thì `arrangePicture` đặt lại cả ba ô (ảnh dọc ⇒ ảnh trái, chữ phải).
    pictureCaption: [
        ['title', 64, 40, 1152, 100],
        ['image', 64, 150, 1152, 430],
        ['caption', 64, 596, 1152, 90],
    ],
    // Nội dung và ảnh: ý bên trái, ảnh bên phải — ô ảnh co theo tỉ lệ ảnh (`arrangePicture`).
    contentPicture: [
        ['title', 64, 40, 1152, 110],
        ['body', 64, 170, 580, 500],
        ['image', 676, 170, 540, 500],
    ],
    // Trang tiêu đề có ảnh (APM-SLD-o): chữ cột trái, ảnh lấp **nửa phải** hết chiều cao.
    // Ảnh chụp ⇒ `cover` + focal (khung cố định); ảnh khác ⇒ `contain` co về đúng tỉ lệ
    // trong nửa phải (`arrangePicture`). 4:3 cũng nửa phải — `reflowAspect` co x/w.
    titlePicture: [
        ['title', 64, 220, 512, 160],
        ['subtitle', 64, 400, 512, 90],
        ['image', 640, 0, 640, 720],
    ],
};
/** Ô đặt sẵn (rỗng) của một layout. `newId` tiêm vào để hàm tất định trong test. */
export function layoutBoxes(layout, aspect, newId) {
    const boxes = LAYOUT_CELLS[layout].map(([role, x, y, w, h]) => ({
        id: newId(),
        role,
        x,
        y,
        w,
        h,
        content: null,
        fileId: null,
        imageId: null,
        ratio: null,
        fit: 'contain',
        focal: null,
    }));
    return aspect === '16:9' ? boxes : boxes.map((box) => reflowBox(box, '16:9', aspect));
}
function reflowBox(box, from, to) {
    if (from === to)
        return box;
    const ratio = SLIDE_FRAME[to].width / SLIDE_FRAME[from].width;
    return { ...box, x: Math.round(box.x * ratio), w: Math.round(box.w * ratio) };
}
/** Đổi tỉ lệ cả bộ: x và w co/giãn theo bề ngang khung, y/h giữ (khung cao 720 ở cả hai). */
export function reflowAspect(slide, from, to) {
    if (from === to)
        return slide;
    return { ...slide, boxes: slide.boxes.map((box) => clampBox(reflowBox(box, from, to), to)) };
}
/** Ô có gì để giữ không — ô rỗng của layout cũ thì bỏ được, ô có chữ/ảnh/hình thì không. */
export function boxHasContent(box) {
    if (box.role === 'image')
        return box.fileId !== null || box.imageId !== null;
    if (box.role === 'shape')
        return true;
    if (box.role === 'table')
        return slideTableOf(box.content) !== null;
    if (box.role === 'chart')
        return Boolean(box.chart && (box.chart.spec || box.chart.snapshot));
    return boxText(box.content).length > 0;
}
/** Vai không thuộc ô nào của layout — đổi layout giữ nguyên chỗ, không đổi thành ô chữ. */
const FREE_ROLES = new Set(['image', 'shape', 'table', 'chart']);
/**
 * Đổi layout (plan §5.2): ô cũ xếp vào ô mới **theo vai, theo thứ tự**; ô cũ có
 * nội dung mà không còn chỗ thì **giữ nguyên vị trí** thành ô `text` (ảnh giữ là
 * ảnh) — không bao giờ mất chữ. Ô đã kéo/đổi cỡ nhận toạ độ của layout mới.
 */
export function applyLayout(slide, next, aspect, newId) {
    const template = layoutBoxes(next, aspect, newId);
    const used = new Set();
    const boxes = template.map((cell) => {
        const match = slide.boxes.find((box) => !used.has(box.id) && box.role === cell.role);
        if (!match)
            return cell;
        used.add(match.id);
        return { ...match, x: cell.x, y: cell.y, w: cell.w, h: cell.h };
    });
    for (const box of slide.boxes) {
        if (used.has(box.id) || !boxHasContent(box))
            continue;
        boxes.push(FREE_ROLES.has(box.role) ? box : { ...box, role: 'text' });
    }
    return arrangePicture({ ...slide, layout: next, boxes: boxes.slice(0, MAX_BOXES_PER_SLIDE) }, aspect);
}
/** Layout có ô ảnh — ảnh (của AI, của "không AI") chỉ gắn vào hai layout này. */
export const PICTURE_LAYOUTS = new Set([
    'pictureCaption',
    'contentPicture',
    'titlePicture',
]);
/** Layout mở được phần Mở đầu: trang tiêu đề, có ảnh hay không (APM-SLD-o). */
export const TITLE_LAYOUTS = new Set(['title', 'titlePicture']);
const PICTURE_MARGIN = 64;
const PICTURE_GAP = 32;
/**
 * Đặt ảnh và chữ theo **tỉ lệ ảnh** — APM-SLD-l. Chỉ cho `pictureCaption` /
 * `contentPicture` có đúng một ô ảnh đã biết `ratio`; còn lại trả nguyên slide.
 *
 * - Nội dung và ảnh: ý trái, ảnh phải; ảnh chiếm ~55% bề ngang nếu ngang, ~50%
 *   nếu vuông, ~40% nếu dọc — ô ý lấy phần còn lại. Ảnh rất rộng (≥ 2,2:1, vd.
 *   biểu đồ dải) ⇒ ảnh trên, ý dưới.
 * - Ảnh có chú thích: ảnh ngang ⇒ tiêu đề trên, ảnh lớn giữa, chú thích ngay dưới
 *   ảnh; ảnh dọc / vuông ⇒ ảnh trái cao hết slide, tiêu đề + chú thích cột phải.
 *
 * Ô ảnh luôn **đúng** tỉ lệ ảnh (không viền trống), căn giữa trong chỗ của nó.
 * Hàm thuần: đổi layout (`applyLayout`), chèn ảnh, và dựng từ đặc tả cùng gọi.
 */
export function arrangePicture(slide, aspect) {
    if (!PICTURE_LAYOUTS.has(slide.layout))
        return slide;
    const images = slide.boxes.filter((box) => box.role === 'image');
    const { width: W, height: H } = SLIDE_FRAME[aspect];
    if (slide.layout === 'titlePicture') {
        // Khung cố định nửa phải: `cover` lấp hết (crop theo focal), `contain` co về tỉ lệ ảnh
        // và căn giữa trong nửa ấy — không viền trống trong ô, không méo.
        const image = images.length === 1 ? images[0] : undefined;
        if (!image)
            return slide;
        const half = { x: W / 2, y: 0, w: W / 2, h: H };
        let rect = half;
        if (image.fit !== 'cover' && image.ratio) {
            let w = half.w;
            let h = w / image.ratio;
            if (h > H) {
                h = H;
                w = h * image.ratio;
            }
            rect = { x: half.x + (half.w - w) / 2, y: (H - h) / 2, w, h };
        }
        return {
            ...slide,
            boxes: slide.boxes.map((box) => box.id === image.id ? clampBox({ ...box, ...rect }, aspect) : box),
        };
    }
    const ratio = images.length === 1 ? images[0].ratio : null;
    if (!ratio)
        return slide;
    const inner = W - 2 * PICTURE_MARGIN;
    const byRole = (role) => slide.boxes.find((box) => box.role === role);
    const fit = (maxW, maxH) => {
        let w = maxW;
        let h = w / ratio;
        if (h > maxH) {
            h = maxH;
            w = h * ratio;
        }
        return { w, h };
    };
    const rects = new Map();
    const put = (box, x, y, w, h) => {
        if (box)
            rects.set(box.id, { x, y, w, h });
    };
    const image = images[0];
    const title = byRole('title');
    if (slide.layout === 'contentPicture') {
        const body = byRole('body');
        const top = 170;
        const areaH = H - 50 - top;
        put(title, PICTURE_MARGIN, 40, inner, 110);
        if (ratio >= 2.2) {
            const { w, h } = fit(inner, areaH * 0.5);
            put(image, PICTURE_MARGIN + (inner - w) / 2, top, w, h);
            put(body, PICTURE_MARGIN, top + h + PICTURE_GAP, inner, areaH - h - PICTURE_GAP);
        }
        else {
            const share = ratio >= 1.5 ? 0.55 : ratio >= 1 ? 0.5 : 0.4;
            const { w, h } = fit(inner * share, areaH);
            put(image, W - PICTURE_MARGIN - w, top + (areaH - h) / 2, w, h);
            put(body, PICTURE_MARGIN, top, inner - w - PICTURE_GAP, areaH);
        }
    }
    else {
        const caption = byRole('caption');
        const captionH = caption && boxText(caption.content) ? 90 : 60;
        if (ratio >= 1.25) {
            const top = 150;
            const areaH = H - 40 - captionH - 16 - top;
            const { w, h } = fit(inner, areaH);
            const y = top + (areaH - h) / 2;
            put(title, PICTURE_MARGIN, 40, inner, 100);
            put(image, PICTURE_MARGIN + (inner - w) / 2, y, w, h);
            put(caption, PICTURE_MARGIN, y + h + 16, inner, captionH);
        }
        else {
            const { w, h } = fit(inner * 0.5, H - 80);
            put(image, PICTURE_MARGIN, (H - h) / 2, w, h);
            const x = PICTURE_MARGIN + w + 48;
            const colW = W - PICTURE_MARGIN - x;
            put(title, x, H / 2 - 190, colW, 160);
            put(caption, x, H / 2 - 20, colW, 240);
        }
    }
    return {
        ...slide,
        boxes: slide.boxes.map((box) => {
            const rect = rects.get(box.id);
            return rect ? clampBox({ ...box, ...rect }, aspect) : box;
        }),
    };
}
// ---------------------------------------------------------------------------
// Chữ trong slide
// ---------------------------------------------------------------------------
const oneLine = (text) => text.replace(/\s+/g, ' ').trim();
function inlineText(node) {
    if (typeof node.text === 'string')
        return node.text;
    if (node.type === 'hardBreak')
        return ' ';
    return (node.content ?? []).map(inlineText).join('');
}
/** Các dòng chữ của một ô: mỗi đoạn / mục danh sách là một dòng. */
export function boxLines(content) {
    const lines = [];
    const walk = (node) => {
        if (node.type === 'paragraph' || node.type === 'heading') {
            const text = oneLine(inlineText(node));
            if (text)
                lines.push(text);
            return;
        }
        for (const child of node.content ?? [])
            walk(child);
    };
    if (content)
        walk(content);
    return lines;
}
export function boxText(content) {
    return boxLines(content).join(' · ');
}
/** Số ký tự chữ của cả slide — so với `MAX_SLIDE_TEXT`. */
export function slideTextLength(slide) {
    return slide.boxes.reduce((sum, box) => sum + boxText(box.content).length, 0);
}
/** Tiêu đề của slide (ô `title` đầu tiên có chữ) — cho danh sách, markdown. */
export function slideTitle(slide) {
    for (const box of slide.boxes) {
        if (box.role !== 'title')
            continue;
        const text = boxText(box.content);
        if (text)
            return text;
    }
    return null;
}
// ---------------------------------------------------------------------------
// Băm nội dung vùng — phát hiện "vùng đã đổi"
// ---------------------------------------------------------------------------
/** FNV-1a 32-bit của markdown vùng sau khi chuẩn hoá khoảng trắng, hex 8 ký tự (plan §3.5). */
export function regionHash(markdown) {
    const text = oneLine(markdown);
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
}
// ---------------------------------------------------------------------------
// Markdown đọc được của một thanh ngăn
// ---------------------------------------------------------------------------
/**
 * Thanh ngăn (plan §3.6) ⇒ dòng chú thích + mỗi slide một dòng: ô tìm kiếm và
 * LLM đọc được chữ trong slide. Đọc thô như `drawing` — slide hỏng schema của
 * bản sau vẫn ra được chữ.
 */
export function slideBreakMarkdown(attrs, label = 'vùng trình chiếu') {
    const raw = attrs?.['slides'];
    const slides = Array.isArray(raw) ? raw : [];
    const lines = [slides.length ? `<!-- ${label}: ${slides.length} slide -->` : `<!-- ${label} -->`];
    slides.forEach((slide, index) => {
        const record = (slide && typeof slide === 'object' ? slide : {});
        const layout = SlideLayout.safeParse(record.layout);
        const label = layout.success ? SLIDE_LAYOUT_LABELS[layout.data] : null;
        const parts = [];
        for (const box of Array.isArray(record.boxes) ? record.boxes : []) {
            if (!box || typeof box !== 'object')
                continue;
            const { role, content, chart } = box;
            if (role === 'image') {
                parts.push('[ảnh]');
                continue;
            }
            if (role === 'chart') {
                const title = chart && typeof chart === 'object'
                    ? chart.spec?.title
                    : null;
                parts.push(typeof title === 'string' && title ? `[biểu đồ: ${oneLine(title)}]` : '[biểu đồ]');
                continue;
            }
            if (role === 'table') {
                const cells = content && typeof content === 'object' ? boxLines(content).join(' · ') : '';
                parts.push(cells ? `[bảng: ${cells}]` : '[bảng]');
                continue;
            }
            const text = content && typeof content === 'object' ? boxLines(content).join(' · ') : '';
            if (text)
                parts.push(text);
        }
        lines.push(`**Slide ${index + 1}${label ? ` — ${label}` : ''}:** ${parts.join(' · ')}`.trimEnd());
    });
    return lines;
}
// ---------------------------------------------------------------------------
// SlideDeckSpec — cái AI (và "Tạo slide từ vùng") sinh ra
// ---------------------------------------------------------------------------
/** Layout AI được chọn — `blank` không có chữ nào để sinh. */
export const SLIDE_SPEC_LAYOUTS = SLIDE_LAYOUTS.filter((layout) => layout !== 'blank');
export const SlideSpecLayout = z.enum(SLIDE_SPEC_LAYOUTS);
/** Số slide chọn được trong hộp AI; "Tự chọn" thì AI dừng ở `SLIDE_AUTO_MAX`. */
export const SLIDE_COUNT_MAX = 15;
export const SLIDE_AUTO_MAX = 12;
/**
 * Mở đầu / Kết thúc: trang tiêu đề + "Nội dung", hay "Tóm tắt" + "Cảm ơn" (APM-SLD-k).
 * Từ APM-SLD-m đây là trần **cứng** của hai vùng ẩn, không chỉ trần "Tự chọn":
 * ép 3 slide vào phần chỉ có chất liệu cho 2 là model phải độn một slide
 * "Tổng quan" trùng với "Nội dung" (lượt thật 29/09).
 */
export const SLIDE_EDGE_AUTO_MAX = 2;
export const SLIDE_REGION_MAX_CHARS = 12_000;
export const SLIDE_OUTLINE_MAX_CHARS = 2_000;
export const SLIDE_SPEC_MAX = 20;
export const SlideBullet = z.object({
    text: z.string().trim().min(1).max(200),
    level: z.number().int().min(0).max(1),
});
export const SlideSpec = z.object({
    layout: SlideSpecLayout,
    title: z.string().trim().min(1).max(120),
    subtitle: z.string().trim().max(200).optional(),
    bullets: z.array(SlideBullet).max(10).optional(),
    columns: z
        .array(z.object({
        heading: z.string().trim().max(80).optional(),
        bullets: z.array(SlideBullet).max(8),
    }))
        .max(2)
        .optional(),
    caption: z.string().trim().max(300).optional(),
    /** Chỉ id có trong `SlideRequest.images` — model không được bịa id ảnh. */
    imageId: z.string().trim().min(1).max(64).optional(),
});
export const SlideDeckSpec = z.object({
    slides: z.array(SlideSpec).min(1).max(SLIDE_SPEC_MAX),
    /** Điều AI (hoặc "không AI") muốn nói với người dùng — vd. vùng quá ngắn cho số slide đã chọn. */
    note: z.string().trim().max(300).nullable(),
});
/** Sức chứa của một layout ở tỉ lệ + chủ đề đang dùng — web ước lượng bằng đo chữ thật. */
export const SlideCapacity = z.object({
    titleChars: z.number().int().min(8).max(200),
    bulletsMax: z.number().int().min(1).max(10),
    bulletChars: z.number().int().min(8).max(200),
});
const SlideRequestImage = z.object({
    /** `fileId` của ảnh tải lên, hoặc `imageId` (`i…`) của ảnh nhúng — slide dùng chung ảnh với tài liệu. */
    id: z.string().trim().min(1).max(64),
    alt: z.string().trim().max(200),
    /** Rộng / cao; `null` = chưa đo được. AI chọn layout theo nó (ảnh dọc, ảnh rất rộng). */
    ratio: z.number().min(0.05).max(20).nullable(),
    /** Mô tả do vision sinh lúc chèn (APM-SLD-n) — có thì prompt đọc nó thay `alt`. */
    description: ImageDescription.nullable().optional(),
});
/**
 * Vùng đang tạo slide (APM-SLD-k): vùng ngăn thường, hay một trong hai vùng
 * ẩn — `opening` (trang tiêu đề) đọc cả tài liệu, `closing` (trang kết) cũng thế.
 */
export const SLIDE_REGION_PARTS = ['opening', 'section', 'closing'];
export const SlideRegionPart = z.enum(SLIDE_REGION_PARTS);
/** Người trình bày ghi lên trang tiêu đề / trang kết — người dùng sửa được trước khi gửi. */
export const SlidePresenter = z.object({
    name: z.string().trim().max(200),
    organization: z.string().trim().max(200),
    /** Ngày hiển thị (`dd/MM/yyyy`), rỗng = không ghi. */
    date: z.string().trim().max(20),
});
/**
 * Mức chi tiết của bộ slide so với nội dung đang soạn — người dùng yêu cầu 2026-10-30. `more` giữ
 * gần đủ ý, số liệu, ví dụ; `less` chỉ giữ ý cốt lõi. Khi số slide là "Tự chọn", nó cũng định
 * hướng dùng nhiều hay ít slide.
 */
export const SlideDetail = z.enum(['more', 'medium', 'less']);
export const SlideRequest = z
    .object({
    /** Markdown của vùng — **dữ liệu**, không phải chỉ dẫn. */
    region: z.string().trim().min(1).max(SLIDE_REGION_MAX_CHARS),
    /** Dàn ý tiêu đề cả tài liệu — ngữ cảnh để hiểu vùng nằm đâu, không phải nội dung. */
    outline: z.string().trim().max(SLIDE_OUTLINE_MAX_CHARS),
    /** `null` = AI tự chọn (≤ `SLIDE_AUTO_MAX`). */
    count: z.number().int().min(1).max(SLIDE_COUNT_MAX).nullable(),
    /** Mức chi tiết so với nội dung vùng — vắng = `medium` (2026-10-30). */
    detail: SlideDetail.default('medium'),
    aspect: SlideAspect,
    theme: SlideThemeId,
    capacity: z.record(SlideSpecLayout, SlideCapacity),
    /** Ảnh có trong vùng — AI chỉ được dùng id ở đây, cho `pictureCaption` / `contentPicture`. */
    images: z.array(SlideRequestImage).max(20),
    /** Số slide vùng đang có — để AI biết đang thêm tiếp hay làm lại. */
    existing: z.number().int().min(0).max(MAX_SLIDES_PER_REGION),
    part: SlideRegionPart.default('section'),
    /** Chỉ với `opening` / `closing`; vùng thường không ghi tên ai lên slide. */
    presenter: SlidePresenter.nullable().default(null),
    /**
     * Tên tệp (không đuôi) — chỉ để **tham khảo** khi tài liệu không có tiêu đề
     * (APM-SLD-m). Trước đây web chèn `# tên tệp` giả làm dòng tiêu đề và prompt
     * bắt giữ "đúng như dữ liệu vào", nên trang tiêu đề mang tên tệp không dấu
     * (`Chia se thu SHR 7`) thay vì tên bài từ nội dung.
     */
    fileName: z.string().trim().max(200).default(''),
})
    .check((ctx) => {
    const { part, count } = ctx.value;
    if (part !== 'section' && count !== null && count > SLIDE_EDGE_AUTO_MAX)
        ctx.issues.push({
            code: 'custom',
            path: ['count'],
            message: `Phần ${part === 'opening' ? 'mở đầu' : 'kết'} tối đa ${SLIDE_EDGE_AUTO_MAX} slide.`,
            input: count,
        });
});
/** `POST /me/files/slide-plan` — APM-SLD-g. `prompt`: yêu cầu thêm của người dùng. */
export const SlideGenerateInput = SlideRequest.extend({
    prompt: z.string().trim().max(500).optional(),
    runId: z.uuid().optional(),
    /**
     * Tầng mô hình người dùng chọn cho lượt sinh này — tốt / trung bình / thấp (người dùng yêu cầu
     * 2026-10-30). Vắng = tầng của skill (`standard`). Tên mô hình của mỗi tầng do tổ chức cấu hình
     * (`ai.model.<tier>`), nên chọn tầng chứ không chọn tên mô hình.
     */
    modelTier: AiModelTier.optional(),
});
export const SlideGenerateResult = z.object({
    spec: SlideDeckSpec,
    /** Chuỗi thập phân như mọi số tiền AI khác — không làm tròn qua `number`. */
    costUsd: UsdAmount,
    partial: z.boolean(),
});
/** Layout có cột (đúng 2). */
const COLUMN_LAYOUTS = new Set(['twoContent', 'comparison']);
/** Layout có ô ý (`body`) — ý đặt vào layout khác là chữ **mất** lúc dựng slide, không ai báo. */
export const BULLET_LAYOUTS = new Set([
    'titleContent',
    'contentCaption',
    'contentPicture',
]);
/** Trần số slide khi "Tự chọn" của một phần. */
export const slideAutoMax = (part) => part === 'section' ? SLIDE_AUTO_MAX : SLIDE_EDGE_AUTO_MAX;
/**
 * Lỗi của một bộ slide so với yêu cầu — `finalize` của skill và bản xem trước
 * ở web cùng đọc một chỗ (plan §8.1). Câu tiếng Việt: gửi lại cho model sửa.
 */
export function slideSpecProblems(spec, request) {
    const problems = [];
    problems.push(...presenterProblems(spec, request.part ?? 'section', request.presenter ?? null));
    const total = spec.slides.length;
    if (request.count !== null && total !== request.count)
        problems.push(`Cần đúng ${request.count} slide, đang có ${total}.`);
    const autoMax = slideAutoMax(request.part ?? 'section');
    if (request.count === null && total > autoMax)
        problems.push(request.part && request.part !== 'section'
            ? `Phần ${request.part === 'opening' ? 'mở đầu' : 'kết'} tự chọn thì tối đa ${autoMax} slide (không chép nội dung các vùng vào đây), đang có ${total}.`
            : `Tự chọn số slide thì tối đa ${autoMax}, đang có ${total}.`);
    const imageIds = new Set(request.images.map((image) => image.id));
    const usedImages = new Set();
    spec.slides.forEach((slide, index) => {
        const at = `Slide ${index + 1}`;
        const capacity = request.capacity[slide.layout];
        if (slide.title.length > capacity.titleChars)
            problems.push(`${at}: tiêu đề dài ${slide.title.length} ký tự, tối đa ${capacity.titleChars}.`);
        const lists = [slide.bullets ?? [], ...(slide.columns ?? []).map((column) => column.bullets)];
        for (const bullets of lists) {
            if (bullets.length > capacity.bulletsMax)
                problems.push(`${at}: ${bullets.length} ý, tối đa ${capacity.bulletsMax} ý mỗi ô.`);
            problems.push(...outlineProblems(at, bullets));
            for (const bullet of bullets)
                if (bullet.text.length > capacity.bulletChars)
                    problems.push(`${at}: ý "${bullet.text.slice(0, 30)}…" dài ${bullet.text.length} ký tự, tối đa ${capacity.bulletChars}.`);
        }
        if (slide.bullets?.length && !BULLET_LAYOUTS.has(slide.layout))
            problems.push(`${at}: layout ${SLIDE_LAYOUT_LABELS[slide.layout]} không có ô ý — ${slide.layout === 'pictureCaption' ? 'dùng contentPicture để có ý cạnh ảnh, hoặc gộp vào `caption`' : 'dùng titleContent'}.`);
        const previous = spec.slides[index - 1];
        if (previous?.layout === 'sectionHeader' && previous.title.trim() === slide.title.trim())
            problems.push(`${at}: slide Đầu phần ngay trước đã cùng tiêu đề "${slide.title}" — bỏ slide Đầu phần thừa.`);
        if (COLUMN_LAYOUTS.has(slide.layout) && (slide.columns?.length ?? 0) !== 2)
            problems.push(`${at}: layout ${SLIDE_LAYOUT_LABELS[slide.layout]} cần đúng 2 cột.`);
        if (slide.imageId !== undefined && !imageIds.has(slide.imageId))
            problems.push(`${at}: ảnh "${slide.imageId}" không có trong danh sách ảnh của vùng.`);
        if (slide.imageId !== undefined && usedImages.has(slide.imageId))
            problems.push(`${at}: ảnh "${slide.imageId}" đã dùng ở slide trước — mỗi ảnh một lần.`);
        if (slide.imageId !== undefined)
            usedImages.add(slide.imageId);
        if (slide.imageId !== undefined && !PICTURE_LAYOUTS.has(slide.layout))
            problems.push(`${at}: layout ${SLIDE_LAYOUT_LABELS[slide.layout]} không có ô ảnh — dùng pictureCaption hoặc contentPicture.`);
        if (slide.layout === 'contentPicture' && slide.imageId === undefined)
            problems.push(`${at}: layout Nội dung và ảnh cần một ảnh trong danh sách ảnh của vùng.`);
        if (slide.layout === 'pictureCaption' && slide.imageId === undefined)
            problems.push(`${at}: layout Ảnh có chú thích cần một ảnh trong danh sách ảnh của vùng.`);
        if (slide.layout === 'titlePicture' && slide.imageId === undefined)
            problems.push(`${at}: layout Trang tiêu đề có ảnh cần một ảnh trong danh sách ảnh — không có thì dùng title.`);
    });
    return problems;
}
/**
 * Dàn ý trong một ô ý chỉ có **hai cấp** và phải đọc được như dàn ý (2026-10-02):
 * ý con đứng đầu ô là mồ côi (không có ý chính nào phía trên — web sẽ vẽ nó thành ý
 * chính, nên lỗi hỏng im lặng); một ý chính duy nhất mang mọi ý con là một tiêu đề
 * phụ đội lốt ý — cấp ấy thừa, slide thành ba cấp chữ cho một tầng nghĩa.
 */
function outlineProblems(at, bullets) {
    const problems = [];
    const first = bullets[0];
    if (first && first.level > 0)
        problems.push(`${at}: ý con "${first.text.slice(0, 30)}" đứng đầu ô mà không có ý chính nào phía trên — đặt level 0, hoặc thêm ý chính trước nó.`);
    const mains = bullets.filter((bullet) => bullet.level === 0);
    const subs = bullets.length - mains.length;
    if (mains.length === 1 && subs >= 2 && first?.level === 0)
        problems.push(`${at}: chỉ một ý chính "${first.text.slice(0, 30)}" mang mọi ý con — ý chính ấy đang làm tiêu đề phụ. Nâng các ý con lên level 0, hoặc tách thành slide riêng lấy ý chính ấy làm tiêu đề.`);
    return problems;
}
/** Chữ của trang tiêu đề/kết có chứa `value` không — không phân biệt hoa thường, khoảng trắng. */
const mentions = (texts, value) => {
    const norm = (text) => text.replace(/\s+/g, ' ').trim().toLowerCase();
    const needle = norm(value);
    return texts.some((text) => norm(text).includes(needle));
};
/**
 * Trang mở đầu phải **mở** bằng slide `title` ghi tên người trình bày và tổ chức;
 * phần kết phải ghi tên người trình bày ở một slide nào đó (APM-SLD-k). Trường rỗng thì
 * không đòi — người dùng xoá đi là không muốn ghi.
 */
function presenterProblems(spec, part, presenter) {
    if (part === 'section')
        return [];
    const problems = [];
    const first = spec.slides[0];
    if (part === 'opening' && first && !TITLE_LAYOUTS.has(first.layout))
        problems.push('Slide 1 của phần mở đầu phải là layout `title` hoặc `titlePicture` (trang tiêu đề).');
    if (!presenter)
        return problems;
    const target = part === 'opening' ? (first ? [first] : []) : spec.slides;
    const texts = target.flatMap((slide) => [slide.title, slide.subtitle ?? '', slide.caption ?? '']);
    const where = part === 'opening' ? 'Trang tiêu đề' : 'Phần kết';
    const required = [['tên người trình bày', presenter.name]];
    if (part === 'opening')
        required.push(['tổ chức', presenter.organization]);
    for (const [label, value] of required)
        if (value && !mentions(texts, value))
            problems.push(`${where} cần ghi ${label} "${value}" (trong \`subtitle\`).`);
    return problems;
}
//# sourceMappingURL=slides.js.map